import React from 'react'
import { Link } from 'react-router-dom'

export default function Terms() {
  const Section = ({ title, children }) => (
    <section className="mt-6">
      <h2 className="text-base font-semibold text-gray-900 mb-2">{title}</h2>
      <div className="text-sm text-gray-700 space-y-2 leading-relaxed">{children}</div>
    </section>
  )

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-50 to-sky-100 py-10 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="flex items-center gap-3 mb-6">
            <img src="/azcclogo.jpg" alt="Arizona Carpet Care" className="h-10 w-auto rounded-md shadow-sm" />
            <div>
              <h1 className="text-xl font-bold text-gray-900">Terms of Use</h1>
              <p className="text-xs text-gray-500">Service Schedule Management System</p>
            </div>
          </div>

          <p className="text-sm text-gray-600">
            This software-as-a-service application (the "System") is developed, owned, and operated by <span className="font-semibold">Tighlman Group LLC</span> ("Developer") and licensed for use by Arizona Carpet Care ("Client"). By accessing the System, you agree to the terms below.
          </p>

          <Section title="1. Ownership & License">
            <p>
              Tighlman Group LLC is the sole and exclusive owner of all right, title, and interest in and to the System, including without limitation all source code, object code, design, user interface, architecture, documentation, configurations, and all related intellectual property rights worldwide. Nothing in this agreement transfers any ownership interest to the Client or to users.
            </p>
            <p>
              Subject to continued compliance with these terms, the Developer grants the Client and its authorized personnel a limited, non-exclusive, non-transferable, non-sublicensable, revocable license to access and use the System solely for the Client's internal business operations.
            </p>
            <p>
              The Client and its users may not, directly or indirectly: (a) copy, reproduce, modify, adapt, translate, or create derivative works of the System; (b) reverse engineer, decompile, disassemble, or attempt to derive source code; (c) sell, lease, sublicense, distribute, or otherwise transfer the System or any portion of it; (d) remove, obscure, or alter any proprietary notices; or (e) use the System to build a competing product or service. The Developer may revoke this license at any time.
            </p>
          </Section>

          <Section title="2. Authorized Use Only">
            <p>
              Access to the System is restricted to personnel expressly authorized by the Client, acting within the scope of that authorization and for legitimate business purposes only. Credential sharing, accessing accounts that are not your own, attempting to circumvent authentication, and any use exceeding the scope of authorization are strictly prohibited.
            </p>
            <p>
              Unauthorized access, attempted access, or misuse of the System may constitute a violation of the federal Computer Fraud and Abuse Act, <span className="font-medium">18 U.S.C. § 1030</span>, as well as comparable state and foreign laws. Violations may result in civil liability, criminal prosecution, and termination of access without notice. The Developer reserves the right to refer suspected violations to law enforcement.
            </p>
          </Section>

          <Section title="3. Confidentiality">
            <p>
              All data, records, and information accessible through the System is confidential and proprietary to the Client. Users may not export, share, copy, screenshot, photograph, transmit, disclose, or otherwise make available any such data to any third party without prior written authorization from the Client.
            </p>
            <p>
              Users must take reasonable measures to safeguard credentials and any data accessed through the System, including but not limited to using secure networks, locking unattended devices, and reporting suspected compromise. Confidentiality obligations under this section survive termination of access for any reason.
            </p>
          </Section>

          <Section title="4. Activity Monitoring">
            <p>
              The Developer and the Client may monitor, record, and review all activity within the System for any purpose, including security, auditing, troubleshooting, performance analysis, legal compliance, and operational oversight. Recorded activity may include, without limitation, sign-in events, changes to records, navigation, and timestamps.
            </p>
            <p>
              By accessing the System, users acknowledge that they have no expectation of privacy in any activity, communication, or content within the System, and they expressly consent to such monitoring and to the use, retention, and disclosure of activity records as the Developer or Client deems appropriate.
            </p>
          </Section>

          <Section title="5. Disclaimer of Warranties">
            <p className="uppercase font-medium tracking-tight">
              The System is provided "AS IS" and "AS AVAILABLE", with all faults and without warranty of any kind. To the maximum extent permitted by applicable law, Tighlman Group LLC expressly disclaims all warranties, whether express, implied, statutory, or otherwise, including without limitation any implied warranties of merchantability, fitness for a particular purpose, title, non-infringement, accuracy, completeness, quiet enjoyment, or arising from course of dealing or usage of trade.
            </p>
            <p className="uppercase font-medium tracking-tight">
              Without limiting the foregoing, the Developer makes no warranty that the System will be uninterrupted, secure, error-free, free of harmful components, or that any data will be accurate, reliable, preserved, restored, or available. No advice or information obtained by users from the Developer or through the System creates any warranty not expressly stated in these terms.
            </p>
          </Section>

          <Section title="6. Limitation of Liability">
            <p className="uppercase font-medium tracking-tight">
              To the maximum extent permitted by applicable law, in no event shall Tighlman Group LLC, its officers, directors, employees, contractors, agents, suppliers, or licensors be liable for any indirect, incidental, special, consequential, punitive, exemplary, or enhanced damages, including without limitation lost profits, lost revenue, lost business, lost data, loss of goodwill, business interruption, cost of substitute services, or any other intangible loss, arising out of or related to the System or these terms, regardless of the legal theory (whether in contract, tort, negligence, strict liability, statute, or otherwise) and whether or not the Developer has been advised of the possibility of such damages.
            </p>
            <p className="uppercase font-medium tracking-tight">
              In any event, the total aggregate liability of Tighlman Group LLC arising out of or related to the System or these terms shall not exceed one hundred US dollars ($100.00). This limitation applies cumulatively to all claims and causes of action, whether asserted by the Client, users, or any third party. The parties acknowledge that these limitations are an essential basis of the bargain and would not be entered into without them.
            </p>
          </Section>

          <Section title="7. No Data Backup Guarantee">
            <p>
              The Developer makes no commitment to back up, archive, restore, replicate, or otherwise preserve any data within the System. The Client is solely and exclusively responsible for maintaining its own independent backups of any information it considers important and for verifying the integrity of such backups.
            </p>
            <p>
              The Developer shall have no liability whatsoever for any loss, corruption, deletion, or unavailability of data, regardless of cause. Users should not rely on the System as a system of record for any data they cannot afford to lose.
            </p>
          </Section>

          <Section title="8. Governing Law & Jurisdiction">
            <p>
              These terms and any dispute arising out of or related to the System or these terms shall be governed by and construed in accordance with the laws of the State of Arizona, without regard to its conflict of law principles.
            </p>
            <p>
              The parties irrevocably consent to the exclusive jurisdiction of, and venue in, the state and federal courts located within the State of Arizona for the resolution of any such dispute. The parties waive any objection to such jurisdiction or venue, including any objection based on forum non conveniens. The United Nations Convention on Contracts for the International Sale of Goods does not apply.
            </p>
          </Section>

          <Section title="9. Modifications">
            <p>
              Tighlman Group LLC may modify, update, or replace these terms at any time and at its sole discretion. The current version will be posted on this page with a revised effective date. It is the user's responsibility to review these terms periodically.
            </p>
            <p>
              Continued access to or use of the System after a modification takes effect constitutes acceptance of the modified terms. If a user does not agree to the modified terms, the user must immediately cease all access to and use of the System.
            </p>
          </Section>

          <Section title="10. Contact">
            <p>
              Legal notices, license inquiries, suspected misuse, or other questions regarding these terms should be directed to Tighlman Group LLC at{' '}
              <a href="mailto:contact@tighlmangroup.com" className="text-brand-600 hover:text-brand-700 font-medium">
                contact@tighlmangroup.com
              </a>.
            </p>
          </Section>

          <div className="mt-8 pt-6 border-t border-gray-100 flex items-center justify-between text-xs text-gray-400">
            <span>Effective: June 2026</span>
            <div className="flex items-center gap-3">
              <Link to="/privacy" className="text-brand-600 hover:text-brand-700 font-medium">
                Privacy Policy
              </Link>
              <Link to="/login" className="text-brand-600 hover:text-brand-700 font-medium">
                ← Back to sign in
              </Link>
            </div>
          </div>
        </div>

        <p className="text-center text-xs text-gray-400 mt-6">
          © {new Date().getFullYear()} Tighlman Group LLC. All rights reserved.{' '}
          <Link to="/privacy" className="text-brand-600 hover:text-brand-700 font-medium">Privacy</Link>
        </p>
      </div>
    </div>
  )
}
